import { useSyncExternalStore } from 'react'

// A set of keys held outside any component, for the two things on the vendor
// record that have to outlive the component that changed them.
//
// # WHY NOT COMPONENT STATE
//
// A reminder in flight belonged to the button that was pressed. The same
// button is drawn on the vendor's line and again under it once Details is
// open, and opening Details takes the first one off the page, so the second
// knew nothing of the request and sent the invitation again (review of
// 11 Oct 2026). Whether Details is open was the same shape of problem: the
// record can be remounted by a change of address and came back shut.
//
// Checked and set in the same turn, so two presses that land before anything
// is drawn again are still one.
//
// # KEPT FOR THE TAB, WHERE ASKED
//
// Given somewhere to keep them, the keys are written there on every change
// and read back when the set is made, so a reload finds them as they were. The
// caller passes the tab's own storage; a set with none lives as long as the
// page does. Storage that is refused (a hardened profile) costs the keeping
// and nothing else.

export type Flags = {
  has: (key: string) => boolean
  set: (key: string, on: boolean) => void
  watch: (fn: () => void) => () => void
}

type Kept = { in: Pick<Storage, 'getItem' | 'setItem'>; as: string }

function read(kept: Kept): string[] {
  try {
    const held: unknown = JSON.parse(kept.in.getItem(kept.as) ?? '[]')
    return Array.isArray(held) ? held.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

export function flags(kept?: Kept): Flags {
  const on = new Set<string>(kept ? read(kept) : [])
  const watchers = new Set<() => void>()
  return {
    has: (key) => on.has(key),
    set: (key, value) => {
      if (on.has(key) === value) return
      if (value) on.add(key)
      else on.delete(key)
      if (kept) {
        try {
          kept.in.setItem(kept.as, JSON.stringify([...on]))
        } catch {
          // Not kept: the set still holds for as long as the page does.
        }
      }
      watchers.forEach((w) => w())
    },
    watch: (fn) => {
      watchers.add(fn)
      return () => {
        watchers.delete(fn)
      }
    },
  }
}

// The tab's storage, or nothing where there is none to use.
export function tabStorage(): Kept['in'] | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage
  } catch {
    return undefined
  }
}

export function useFlag(f: Flags, key: string): boolean {
  return useSyncExternalStore(
    f.watch,
    () => f.has(key),
    () => f.has(key),
  )
}
